import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ArrowLeft, Mail, Phone, Building2, FileText, FolderKanban, IndianRupee, Plus, MapPin, User, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export const ClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchClientData = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}/overview`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleQuickAction = (action) => {
    switch (action) {
      case 'task':
        navigate(`/tasks?new=true&client_id=${clientId}&client_name=${encodeURIComponent(data.client.name)}`);
        break;
      case 'project':
        navigate(`/projects?new=true&client_id=${clientId}&client_name=${encodeURIComponent(data.client.name)}`);
        break;
      case 'proposal':
        navigate(`/proposals?new=true&client_id=${clientId}&client_name=${encodeURIComponent(data.client.name)}${data.client.company_id ? `&company_id=${data.client.company_id}` : ''}`);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!data) {
    return <Card className="p-12 text-center"><p className="text-muted-foreground">Client not found</p></Card>;
  }

  const { client, projects, proposals, finance } = data;

  return (
    <div data-testid="client-detail-page">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/clients')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Clients
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">{client.name}</h1>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-2 py-0.5 text-xs rounded-full ${client.company_id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                {client.contact_type === 'corporate' || client.company_id ? 'Corporate' : 'Individual'}
              </span>
              {client.position && (
                <span className="text-muted-foreground">{client.position}</span>
              )}
            </div>
            {client.company && <p className="text-lg text-muted-foreground flex items-center gap-2"><Building2 className="h-5 w-5" />{client.company}</p>}
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Quick Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleQuickAction('task')}>
                  <CheckSquare className="h-4 w-4 mr-2" />
                  New Task
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleQuickAction('project')}>
                  <FolderKanban className="h-4 w-4 mr-2" />
                  New Project
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleQuickAction('proposal')}>
                  <FileText className="h-4 w-4 mr-2" />
                  New Proposal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="px-3 py-1 text-sm font-semibold rounded-full bg-chart-4/20 text-chart-4">
              {client.status}
            </span>
          </div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {client.email && (
          <Card className="p-4 flex items-center gap-3">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{client.email}</p>
            </div>
          </Card>
        )}
        {client.phone && (
          <Card className="p-4 flex items-center gap-3">
            <Phone className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Phone</p>
              <p className="font-medium">{client.phone}</p>
            </div>
          </Card>
        )}
        {client.position && (
          <Card className="p-4 flex items-center gap-3">
            <User className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Designation</p>
              <p className="font-medium">{client.position}</p>
            </div>
          </Card>
        )}
        {client.business_address && (
          <Card className="p-4 flex items-center gap-3">
            <MapPin className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Business Address</p>
              <p className="font-medium text-sm">{client.business_address}</p>
            </div>
          </Card>
        )}
      </div>

      {/* Additional Details */}
      {(client.gst_number || client.pan_number || (client.custom_fields && Object.keys(client.custom_fields).length > 0) || client.notes) && (
        <Card className="p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">Additional Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {client.gst_number && (
              <div>
                <p className="text-sm text-muted-foreground">GST Number</p>
                <p className="font-medium">{client.gst_number}</p>
              </div>
            )}
            {client.pan_number && (
              <div>
                <p className="text-sm text-muted-foreground">PAN Number</p>
                <p className="font-medium">{client.pan_number}</p>
              </div>
            )}
            {client.notes && (
              <div className="col-span-full">
                <p className="text-sm text-muted-foreground">Notes</p>
                <p className="font-medium">{client.notes}</p>
              </div>
            )}
            {client.custom_fields && Object.keys(client.custom_fields).length > 0 && (
              <div className="col-span-full">
                <p className="text-sm text-muted-foreground mb-2">Custom Fields</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(client.custom_fields).map(([key, value]) => (
                    <div key={key} className="bg-muted px-3 py-1 rounded text-sm">
                      <strong>{key}:</strong> {value}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <FolderKanban className="h-5 w-5 text-chart-1" />
            <p className="text-sm text-muted-foreground">Projects</p>
          </div>
          <p className="text-3xl font-heading font-bold">{projects.total}</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-5 w-5 text-chart-2" />
            <p className="text-sm text-muted-foreground">Proposals</p>
          </div>
          <p className="text-3xl font-heading font-bold">{proposals.total}</p>
        </Card>

        {finance && user?.role && ['admin', 'manager', 'finance'].includes(user.role) && (
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <IndianRupee className="h-5 w-5 text-chart-4" />
              <p className="text-sm text-muted-foreground">Revenue</p>
            </div>
            <p className="text-3xl font-heading font-bold">INR {finance.total_revenue.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">INR {finance.pending_revenue.toLocaleString()} pending</p>
          </Card>
        )}
      </div>

      <Tabs defaultValue="projects" className="space-y-6">
        <TabsList>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="proposals">Proposals</TabsTrigger>
          {finance && <TabsTrigger value="finance">Finance</TabsTrigger>}
        </TabsList>

        <TabsContent value="projects" className="space-y-4">
          {projects.list.length === 0 ? (
            <Card className="p-8 text-center"><p className="text-muted-foreground">No projects yet</p></Card>
          ) : (
            projects.list.map((project) => (
              <Card key={project.project_id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium mb-1">{project.name}</h4>
                    <p className="text-sm text-muted-foreground">{project.description}</p>
                  </div>
                  <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-chart-4/20 text-chart-4">
                    {project.status}
                  </span>
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="proposals" className="space-y-4">
          {proposals.list.length === 0 ? (
            <Card className="p-8 text-center"><p className="text-muted-foreground">No proposals yet</p></Card>
          ) : (
            proposals.list.map((proposal) => (
              <Card key={proposal.proposal_id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium mb-1">{proposal.title}</h4>
                    {proposal.amount && <p className="text-sm text-muted-foreground">INR {proposal.amount.toLocaleString()}</p>}
                  </div>
                  <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                    proposal.status === 'approved' ? 'bg-chart-4/20 text-chart-4' : 'bg-muted text-foreground'
                  }`}>
                    {proposal.status}
                  </span>
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        {finance && (
          <TabsContent value="finance" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="p-4">
                <p className="text-sm text-muted-foreground mb-1">Total Project Value</p>
                <p className="text-2xl font-heading font-bold">INR {finance.total_project_value?.toLocaleString() || 0}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground mb-1">Revenue Received</p>
                <p className="text-2xl font-heading font-bold text-green-600">INR {finance.total_revenue?.toLocaleString() || 0}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground mb-1">Pending Revenue</p>
                <p className="text-2xl font-heading font-bold text-amber-600">INR {finance.pending_revenue?.toLocaleString() || 0}</p>
              </Card>
            </div>
            <Card className="p-6">
              <h4 className="font-medium mb-4">Fee Structure Summary</h4>
              <p className="text-muted-foreground text-center py-4">
                {finance.fee_items_count || 0} fee items tracked
              </p>
            </Card>
          </TabsContent>
        )}

      </Tabs>
    </div>
  );
};
